import React, { useRef } from "react";
import {
  motion,
  useInView,
  useMotionValue,
  useTransform,
  animate,
} from "framer-motion";
import "./Growth.scss";

import NumberTicker from "../../shared/number-ticker/NumberTicker";

const GrowthCard = ({ card, index, isMobile, cardVariants }) => {
  const cardRef = useRef(null);
  const isInView = useInView(cardRef, { once: true, margin: "0px 0px -50px 0px" });

  return (
    <motion.div
      ref={cardRef}
      key={`${index}-${isMobile ? "mobile" : "desktop"}`}
      className={`growth-card ${card.className}`}
      variants={cardVariants}
    >
      <h2 className="card-number">
        <NumberTicker value={card.number} trigger={isInView} />
      </h2>
      <p className="card-label">{card.label}</p>
      <img src={card.icon} alt={card.label} className="card-icon" />
    </motion.div>
  );
};

const Growth = () => {
  const headingRef = useRef(null);
  const isInView = useInView(headingRef, {
    margin: "0px 0px -50% 0px",
    once: true,
  });

  // Initialize state based on window width if available to avoid flash
  const [isMobile, setIsMobile] = React.useState(() => {
    if (typeof window !== "undefined") {
      return window.innerWidth <= 1024;
    }
    return false;
  });

  React.useEffect(() => {
    const handleResize = () => {
      setIsMobile(window.innerWidth <= 1024);
    };

    window.addEventListener("resize", handleResize);
    return () => window.removeEventListener("resize", handleResize);
  }, []);

  const cards = [
    {
      number: "50+",
      label: "Clients Served",
      className: "blue-card",
      icon: "/images/home/growth/clients-served.webp",
    },
    {
      number: "10+",
      label: "Sectors Served",
      className: "orange-card",
      icon: "/images/home/growth/industries-covered.webp",
    },
    {
      number: "50+",
      label: "Cumulative Years of Experience",
      className: "white-card",
      icon: "/images/home/growth/advisory-experience.webp",
    },
  ];

  const containerVariants = {
    hidden: { opacity: 0 },
    visible: {
      opacity: 1,
      transition: {
        staggerChildren: 0.5,
      },
    },
  };

  const cardVariants = isMobile
    ? {
        // Mobile/Tablet: Expand/Wipe from left using clip-path for a premium reveal
        hidden: { clipPath: "inset(0 100% 0 0)", opacity: 0 },
        visible: {
          clipPath: "inset(0 0 0 0)",
          opacity: 1,
          transition: {
            duration: 0.8,
            ease: [0.22, 1, 0.36, 1],
          },
        },
      }
    : {
        // Desktop: Slide up with opacity
        hidden: { y: 100, opacity: 0 },
        visible: {
          y: 0,
          opacity: 1,
          transition: {
            duration: 0.8,
            ease: [0.22, 1, 0.36, 1],
          },
        },
      };

  return (
    <section className="growth-section">
      <div className="container">
        <h2 ref={headingRef} className="growth-heading">
          Here’s a snapshot of the growth, clarity, and performance we’ve
          enabled - with much more ahead.
          {/* — */}
        </h2>

        <motion.div
          className="growth-cards-container"
          variants={containerVariants}
          initial="hidden"
          animate={isInView ? "visible" : "hidden"}
        >
          {cards.map((card, index) => (
            <GrowthCard
              key={index}
              card={card}
              index={index}
              isMobile={isMobile}
              cardVariants={cardVariants}
            />
          ))}
        </motion.div>
      </div>
    </section>
  );
};

export default Growth;
